import config from 'config';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';
import WarningIconSm from 'components/Icons/WarningIconSm';
import SubmitButton from 'components/SubmitButton';
import patterns from 'helpers/patterns';
import {
  AppleAuthButton,
  GoogleAuthButton,
} from 'modules/corporateOffer/components/authButtons';
import { ContinueWith } from 'modules/corporateOffer/components/authButtons/ContinueWith';
import { useSocialSignInAndApplyOffer } from 'modules/corporateOffer/hooks/useSignInAndApplyOffer';
import { useSignUp } from 'modules/corporateOffer/hooks/useSignUp';
import theme from 'theme/theme';

import Input from '../inputs/Input';
import { ErrorText } from '../inputs/StyledFormInputs';

import { SignInForm, SocialButtonsWrapper } from './SignInStep';
import { OnboardingSubtitle, OnboardingTitle } from './StyledComponents';
import { EMAIL_INPUT_ID, emailValidationSchema } from './validationSchemas';

const FIRST_NAME_MAX_LENGTH = 50;

const NAME_INPUT_ID = 'NAME_INPUT_ID';

const validationSchema = yup
  .object({
    [NAME_INPUT_ID]: yup
      .string()
      .trim()
      .max(
        FIRST_NAME_MAX_LENGTH,
        `The length of the first name must be ${FIRST_NAME_MAX_LENGTH} characters or fewer`
      )
      .matches(patterns.namePattern, {
        message: 'Invalid name format',
        excludeEmptyString: true,
      })
      .required('This field is required'),
  })
  .concat(emailValidationSchema);

type SignUpData = yup.InferType<typeof validationSchema>;

type Props = {
  account: string;
  monthTerm: string | undefined;
  defaultValues: { email: string; name: string } | undefined;
  onSuccess: (email: string, name: string) => void;
};

const SignUpStep: FC<Props> = ({
  account,
  monthTerm,
  onSuccess,
  defaultValues,
}) => {
  const [authError, setAuthError] = useState('');

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    getValues,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      EMAIL_INPUT_ID: defaultValues?.email || '',
      NAME_INPUT_ID: defaultValues?.name || '',
    },
  });

  const {
    mutate: signUp,
    isLoading: isSigningUp,
    isError: isSignUpError,
  } = useSignUp({
    onSuccess: () => {
      onSuccess(getValues(EMAIL_INPUT_ID), getValues(NAME_INPUT_ID));
    },
    onError: (err) => setAuthError(err),
  });
  const {
    mutate: mutateSocial,
    isLoading: isLoadingSocial,
    isError: isErrorSocial,
  } = useSocialSignInAndApplyOffer(account);
  const isError = isSignUpError || isErrorSocial;
  const isLoading = isSigningUp || isLoadingSocial;

  const onSubmit = async (data: SignUpData) => {
    signUp({
      email: data.EMAIL_INPUT_ID,
      firstName: data.NAME_INPUT_ID,
    });
  };

  return (
    <Container>
      <header>
        <DesktopTitle
          title={`Create an account to get ${monthTerm} premium for free`}
          error={isError}
        />
        <MobileTitle title="Create an account" error={isError} />
        <DesktopSubtitle
          subtitle="You will use this to sign in to your Shido mobile app"
          error={isError}
        />
        <MobileSubtitle
          subtitle="You will use this to sign-in to your app"
          error={isError}
        />
      </header>
      <SignInForm onSubmit={handleSubmit(onSubmit)}>
        <Input<SignUpData>
          label="Enter your first name"
          inputId={NAME_INPUT_ID}
          error={errors.NAME_INPUT_ID}
          register={register}
          control={control}
        />
        <Input<SignUpData>
          label="Enter your email address"
          inputId={EMAIL_INPUT_ID}
          inputType="email"
          control={control}
          register={register}
          error={errors.EMAIL_INPUT_ID}
        />
        {!!authError && (
          <ErrorText $error={!!authError}>
            <WarningIconSm color={theme.colors.primary} />
            {authError}
          </ErrorText>
        )}
        <ButtonWrapper data-testid="submit-button-wrapper">
          <SubmitButton
            text="CONTINUE"
            isDisabled={Object.keys(errors).length > 0 || isLoading}
            borderSize="xl"
            buttonSize="xl"
            fontSize="md"
          />
        </ButtonWrapper>
      </SignInForm>

      <ContinueWith />

      <SocialButtonsWrapper>
        <AppleAuthButton
          disabled={isLoading}
          onSuccess={(data) => {
            mutateSocial({ token: data.token });
          }}
          onError={setAuthError}
        />
        <GoogleAuthButton
          disabled={isLoading}
          onSuccess={(data) => {
            mutateSocial({ token: data.token });
          }}
          onError={setAuthError}
        />
      </SocialButtonsWrapper>
      <PrivacyWrapper>
        <PrivacyLink
          href={`${config.shidoWebsiteVersion}/terms-and-conditions`}
          target="_blank"
          rel="noreferrer"
        >
          Terms and conditions
        </PrivacyLink>
        <Divider />
        <PrivacyLink
          href={`${config.shidoWebsiteVersion}/terms-and-conditions`}
          target="_blank"
          rel="noreferrer"
        >
          Privacy policy
        </PrivacyLink>
      </PrivacyWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MobileTitle = styled(OnboardingTitle)`
  display: none;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: block;
  }
`;

const DesktopTitle = styled(OnboardingTitle)`
  display: block;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const MobileSubtitle = styled(OnboardingSubtitle)`
  display: none;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: block;
  }
`;

const DesktopSubtitle = styled(OnboardingSubtitle)`
  display: block;

  ${({ theme }) => theme.breakpoints.down('md')} {
    display: none;
  }
`;

const ButtonWrapper = styled.div`
  margin: 38px auto 0;
  display: flex;
  justify-content: center;
`;

const PrivacyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 80px;
  padding-bottom: 40px;
`;

const PrivacyLink = styled.a`
  color: ${({ theme }) => theme.colors.funtainBlue};
  font-size: 16px;
  text-decoration: none;
`;

const Divider = styled.div`
  background-color: ${({ theme }) => theme.colors.darkGray};
  width: 1px;
  height: 12px;
`;

export default SignUpStep;
